// TanStack Query data layer for the renderer. Two modes:
//
//   - fixture mode (Phase 1): returns the slice-1 FIXTURE_DOC /
//     FIXTURE_GLOSSARY synchronously. The renderer ships and is testable
//     before the backend `/docs/:id/render` route exists.
//   - live mode (Phase 2a): real HTTP fetch against
//     `${VITE_API_BASE}/docs/:docId/render`.
//
// Phase 2a flips `useDoc` to call `useLiveDoc`. Keep both implementations
// here so the swap is one route-component import change.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type {
  CorpusConfig,
  CorpusGlossary,
  DocJSON,
  EquationEntry,
  PublishedGlossary,
} from "./types";
import { CORPUS_CONFIG_DEFAULTS } from "./types";
import { FIXTURE_DOC, FIXTURE_GLOSSARY } from "./fixture";

export interface RenderPayload {
  doc: DocJSON;
  glossary: PublishedGlossary;
  // PRD 08 — composite payload now carries the doc's corpus config so the
  // renderer can apply theme/accent/rail position from one fetch.
  config?: CorpusConfig;
  // PRD 07 — when equation extraction has run, the renderer gets the
  // published equation entries so EquationBlock can show per-symbol
  // tooltips. Absent on equation-less docs (the API-docs demo corpus).
  equations?: { equations: EquationEntry[] } | { equations?: EquationEntry[] };
  // 00_processor §Pass 2 — when Pass 2 has run for this doc's corpus,
  // the canonical entity index ships alongside. The renderer prefers
  // the canonical Definition over the per-doc definition when a term /
  // symbol ref appears in any entity's member_refs.
  corpus_glossary?: CorpusGlossary;
}

export interface UseDocOptions {
  preview?: boolean;
}

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

export function useLiveDoc(
  docId: string,
  opts: UseDocOptions = {},
): UseQueryResult<RenderPayload, Error> {
  const preview = opts.preview === true;
  return useQuery<RenderPayload, Error>({
    queryKey: ["doc", docId, { preview }],
    queryFn: async () => {
      // API_BASE may be relative ("/api" in deployed builds where Caddy
      // proxies same-origin). URL constructor needs a base for relative
      // paths — fall back to window.location.origin so both absolute
      // ("http://localhost:8000") and relative bases construct cleanly.
      const url = new URL(
        `${API_BASE}/docs/${docId}/render`,
        typeof window !== "undefined" ? window.location.origin : undefined,
      );
      if (preview) url.searchParams.set("preview", "1");
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        throw new Error(`fetch ${docId} failed: ${res.status}`);
      }
      return (await res.json()) as RenderPayload;
    },
    staleTime: 60_000,
  });
}

// Fixture variant — synchronous payload via a resolved query. Phase 1 uses
// this so the renderer hydrates without a backend.
export function useFixtureDoc(
  docId: string,
  _opts: UseDocOptions = {},
): UseQueryResult<RenderPayload, Error> {
  return useQuery<RenderPayload, Error>({
    queryKey: ["doc-fixture", docId],
    queryFn: () =>
      Promise.resolve({
        doc: FIXTURE_DOC,
        glossary: FIXTURE_GLOSSARY,
        config: CORPUS_CONFIG_DEFAULTS,
      }),
    staleTime: Infinity,
  });
}

// Default export points at fixture mode for Phase 1. Phase 2a swaps the
// alias to `useLiveDoc`.
export const useDoc = useFixtureDoc;
